import React from 'react';
import { Button } from './ui/button';
import { Shield, Award, Users, MapPin, ArrowRight, CheckCircle } from 'lucide-react';
import { companyInfo } from '../mock';

export const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen bg-gradient-to-br from-slate-900 via-orange-900 to-yellow-900 pt-16 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 border border-orange-300 rounded-full"></div>
        <div className="absolute top-40 right-20 w-96 h-96 border border-yellow-300 rounded-full"></div>
        <div className="absolute bottom-20 left-1/3 w-64 h-64 border border-orange-400 rounded-full"></div>
      </div>

      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="grid lg:grid-cols-12 gap-8 items-center min-h-[80vh]">
          {/* Left Content - Takes 7 columns */}
          <div className="lg:col-span-7 text-white space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border border-white/20">
              <Shield className="h-5 w-5 text-orange-300" />
              <span className="text-orange-100 font-medium">Premier Oil & Gas Training Institute</span>
            </div>
            
            <div className="space-y-6">
              <h1 className="text-5xl lg:text-7xl font-black leading-tight">
                <span className="block text-white">PHOENIX</span>
                <span className="block bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent">
                  WELLCONTROL
                </span>
                <span className="block text-orange-200 text-4xl lg:text-5xl font-bold">ACADEMY</span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-orange-100 leading-relaxed max-w-2xl">
                {companyInfo.heroSubtitle}
              </p>
            </div>

            {/* Key Features */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-6">
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-orange-100">State-of-art Training Facility</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-orange-100">Virtual & Classroom Options</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-orange-100">Industry Expert Instructors</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-orange-100">International Standards</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                size="lg"
                onClick={() => scrollToSection('courses')}
                className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white text-lg px-8 py-6 h-auto rounded-xl font-semibold shadow-2xl hover:shadow-orange-500/25 transition-all duration-300 hover:-translate-y-1"
              >
                Explore Training Programs
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => scrollToSection('contact')}
                className="border-2 border-orange-400 text-orange-200 hover:bg-orange-500 hover:text-white text-lg px-8 py-6 h-auto rounded-xl font-semibold bg-white/10 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1"
              >
                Get Certified Today
              </Button>
            </div>
          </div>

          {/* Right Content - Logo & Stats - Takes 5 columns */}
          <div className="lg:col-span-5 relative">
            {/* Floating Logo */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400/20 to-yellow-400/20 rounded-3xl blur-3xl"></div>
              <div className="relative bg-white/10 backdrop-blur-xl p-8 rounded-3xl border border-white/20 shadow-2xl">
                <img 
                  src={companyInfo.logoUrl} 
                  alt="Phoenix Well Control Academy" 
                  className="h-48 w-48 object-contain mx-auto"
                />
                <div className="text-center mt-6">
                  <p className="text-2xl font-bold text-white italic">
                    "{companyInfo.tagline}"
                  </p>
                </div>
              </div>
            </div>

            {/* Floating Stats Cards */}
            <div className="grid grid-cols-2 gap-4 mt-8">
              <div className="bg-white/10 backdrop-blur-xl p-6 rounded-2xl border border-white/20 text-center transform hover:scale-105 transition-transform">
                <Award className="h-8 w-8 text-orange-400 mx-auto mb-3" />
                <div className="text-3xl font-bold text-white">6</div>
                <div className="text-sm text-orange-200">Specialized Courses</div>
              </div>
              <div className="bg-white/10 backdrop-blur-xl p-6 rounded-2xl border border-white/20 text-center transform hover:scale-105 transition-transform">
                <Users className="h-8 w-8 text-yellow-400 mx-auto mb-3" />
                <div className="text-3xl font-bold text-white">500+</div>
                <div className="text-sm text-orange-200">Professionals Trained</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" className="w-full h-20 fill-white">
          <path d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
        </svg>
      </div>
    </section>
  );
};